import style from './SupplierPricePicker.module.scss';

import { Typography } from '@mui/material';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import React, {
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { EntityPickerTabbed, TabsConfiguration } from '@work4all/components';

import { IResponse } from '@work4all/data';

import { ArticlePurchaseInformation } from '@work4all/models/lib/Classes/ArticlePurchaseInformation.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { formatCurrency, formatNumberAsCurrency } from '@work4all/utils';
import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

import { CurrencyExchangeInfoContext } from '../../../../../containers/mask-overlays/mask-overlay/views/inbound-invoice/currency-exchange-info-context';

// Column width definitions in pixels
const columnWidths = [
  230, // Supplier
  80, // Number
  80, // List Price / Purchase Price
  80, // Discount
  80, // Purchase Price (Supplier tab only)
  80, // Date
  150, // Source
];

interface Props {
  onChange: (item: ArticlePurchaseInformation) => void;
  data: ArticlePurchaseInformation[];
  articleId: number;
}

export const PositionListPicker = (props: Props) => {
  const { onChange, data, articleId } = props;
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('SUPPLIER');

  const currencyExchangeInfo = useContext(CurrencyExchangeInfoContext);
  const defaultCurrency = currencyExchangeInfo?.defaultCurrency;

  const renderHeader = useCallback(
    (isSupplierTab = false) => {
      return (
        <div className={style.header}>
          <div className={style.cell} style={getWidth(columnWidths[0])}>
            <Typography className={style['header-text']}>
              {t('COMMON.SUPPLIER')}
            </Typography>
          </div>
          <div className={style.cell} style={getWidth(columnWidths[1])}>
            <Typography className={style['header-text']}>
              {t('COMMON.NUMBER')}
            </Typography>
          </div>
          <div
            className={clsx(style.cell, style.right)}
            style={getWidth(columnWidths[2])}
          >
            <Typography className={style['header-text']}>
              {isSupplierTab
                ? t('MASK.LIST_PRICE')
                : t('COMMON.ERP.SUPPLIER_PRICE.PURCHASE')}
            </Typography>
          </div>
          <div
            className={clsx(style.cell, style.right)}
            style={getWidth(columnWidths[3])}
          >
            <Typography className={style['header-text']}>
              {t('COMMON.ERP.DISCOUNT')}
            </Typography>
          </div>
          {isSupplierTab && (
            <div
              className={clsx(style.cell, style.right)}
              style={getWidth(columnWidths[4])}
            >
              <Typography className={style['header-text']}>
                {t('COMMON.ERP.SUPPLIER_PRICE.PURCHASE')}
              </Typography>
            </div>
          )}
          <div className={style.cell} style={getWidth(columnWidths[5])}>
            <Typography className={style['header-text']}>
              {isSupplierTab ? t('MASK.VALID_FROM') : t('COMMON.DATE')}
            </Typography>
          </div>

          {!isSupplierTab && (
            <div className={style.cell} style={getWidth(columnWidths[6])}>
              <Typography className={style['header-text']}>
                {t('COMMON.SOURCE')}
              </Typography>
            </div>
          )}
        </div>
      );
    },
    [t]
  );

  const renderItemContent = useCallback(
    (
      supplier: Supplier | ArticlePurchaseInformation,
      isSupplierTab = false
    ) => {
      const row: ArticlePurchaseInformation =
        supplier.__typename === 'Lieferant' ? { supplier } : supplier;

      const isDisabled =
        defaultCurrency &&
        row.currency &&
        defaultCurrency.id !== row.currency.id;

      const dateCell = isSupplierTab ? row.validFromDate : row.objectDate;

      const date = DateTime.fromISO(dateCell);
      return (
        <div className={clsx(style.row, { [style.disabled]: isDisabled })}>
          <div className={style.cell} style={getWidth(columnWidths[0])}>
            <Typography>{row.supplier?.name}</Typography>
          </div>
          <div className={style.cell} style={getWidth(columnWidths[1])}>
            <Typography>{row.objectNumber || row.supplier?.number}</Typography>
          </div>
          <div
            className={clsx(style.cell, style.right)}
            style={getWidth(columnWidths[2])}
          >
            <Typography>
              {`${formatNumberAsCurrency(
                isSupplierTab ? row.purchasePriceListPrice : row.price,
                {
                  maximum: 2,
                  minimum: 2,
                }
              )} ${formatCurrency(row.currency, t)}`}
            </Typography>
          </div>
          <div
            className={clsx(style.cell, style.right)}
            style={getWidth(columnWidths[3])}
          >
            {row.purchasePriceDiscount ? (
              <Typography>{`${row.purchasePriceDiscount} %`}</Typography>
            ) : (
              <></>
            )}
          </div>

          {isSupplierTab && (
            <div
              className={clsx(style.cell, style.right)}
              style={getWidth(columnWidths[4])}
            >
              <Typography>
                {`${formatNumberAsCurrency(row.price, {
                  maximum: 2,
                  minimum: 2,
                })} ${formatCurrency(row.currency, t)}`}
              </Typography>
            </div>
          )}

          <div className={style.cell} style={getWidth(columnWidths[5])}>
            <Typography>
              {`${
                date.isValid && date.get('year') > 1970
                  ? date.toLocaleString(DateTimeCustom.DATE_SHORT)
                  : ''
              }`}
            </Typography>
          </div>

          {!isSupplierTab && (
            <div className={style.cell} style={getWidth(columnWidths[6])}>
              <Typography>
                {t(`COMMON.ERP.SUPPLIER_PRICE.${row.purchaseInfoType}`)}
              </Typography>
            </div>
          )}
        </div>
      );
    },
    [defaultCurrency, t]
  );

  const tabs = useMemo((): TabsConfiguration<
    Supplier | ArticlePurchaseInformation
  >[] => {
    return [
      {
        loadingConfig: {
          entity: Entities.supplier,
          data: SUPPLIER_FIELDS,
          filterBy: ['name', 'number'],
          sortBy: 'name',
          transformResponse: (response: IResponse<Supplier>) => {
            return {
              ...response,
              data: response.data.filter(
                (x) =>
                  !data
                    .filter(
                      (x) => x.purchaseInfoType === 'ARTICLE_PURCHASE_PRICE'
                    )
                    .some((y) => x.id === y.supplier?.id)
              ),
            };
          },
        },
        pickerConfig: {
          label: t('COMMON.SUPPLIER'),
          placeholder: t('SUPPLIER.SEARCH_PLACEHOLDER'),
          prependList: renderHeader(true),
          renderItemContent: (supplier) => renderItemContent(supplier, true),
          additionalItems: data.filter(
            (x) => x.purchaseInfoType === 'ARTICLE_PURCHASE_PRICE'
          ),
        },
      },
      {
        loadingConfig: {
          entity: Entities.articlePurchaseInformation,
          data: {
            price: null,
            objectId: null,
            supplier: SUPPLIER_FIELDS,
            currency: {
              id: null,
              name: null,
            },
            objectDate: null,
            objectNumber: null,
            purchaseInfoType: null,
            purchasePriceListPrice: null,
            purchasePriceDiscount: null,
            validFromDate: null,
          },
          sortBy: 'price',
          filterBy: ['name', 'number'],
          vars: {
            articleCode: articleId,
          },
          transformResponse: (
            response: IResponse<ArticlePurchaseInformation>
          ) => {
            return {
              ...response,
              data: response.data.filter(
                (x) => x.purchaseInfoType !== 'ARTICLE_PURCHASE_PRICE'
              ),
            };
          },
        },
        pickerConfig: {
          label: t(`COMMON.HISTORY`),
          prependList: renderHeader(),
          renderItemContent: (supplier) => renderItemContent(supplier),

          hideSearchBar: true,
        },
      },
    ];
  }, [renderHeader, renderItemContent, t, data, articleId]);

  return (
    <EntityPickerTabbed
      getTabIndex={() => (activeTab === 'SUPPLIER' ? 0 : 1)}
      onChange={(supplier: Supplier | ArticlePurchaseInformation) => {
        const isSupplierTab = supplier.__typename === 'Lieferant';
        const row: ArticlePurchaseInformation = isSupplierTab
          ? { supplier }
          : supplier;

        const isDisabled =
          defaultCurrency &&
          row.currency &&
          defaultCurrency.id !== row.currency.id;
        if (!isDisabled) onChange(row);
      }}
      value={null}
      onTabChange={(entity) => {
        setActiveTab(entity);
      }}
      tabs={tabs}
    />
  );
};

const SUPPLIER_FIELDS = {
  id: null,
  name: null,
  number: null,
};

const getWidth = (width: number): CSSProperties => {
  return {
    width,
    maxWidth: width,
    minWidth: width,
  };
};
